import { along as turfAlong } from '@turf/along';
import { bearing as turfBearing } from '@turf/bearing';
import { lineString as turfLineString, point as turfPoint } from '@turf/helpers';
import { length as turfLength } from '@turf/length';

import { PositionInterface } from '@/types';

import { distanceInKmBetweenEarthCoordinates } from '../helpers/distance-between-points';

/**
 * Calculates the total distance of a polyline path
 * @param positions Array of positions forming the path
 * @returns Total distance in kilometers
 */
export const calculatePathDistance = (positions: PositionInterface[]): number => {
  const allButLastPosition = positions.slice(0, -1);
  return allButLastPosition.reduce((total, pos, i) => {
    return total + distanceInKmBetweenEarthCoordinates(pos, positions[i + 1]);
  }, 0);
};

/**
 * Calculates the bearing in degrees between two points
 * @param start Starting position
 * @param end Ending position
 * @returns Bearing in degrees (0-360)
 */
export const calculateBearing = (start: PositionInterface, end: PositionInterface): number => {
  // Note order: longitude, latitude
  const point1 = turfPoint([start.lng, start.lat]);
  const point2 = turfPoint([end.lng, end.lat]);

  return turfBearing(point1, point2);
};

/**
 * Finds a point along a polyline at a specific fraction of the total distance
 * @param positions The positions that make up the polyline
 * @param fraction A number between 0 and 1 representing how far along the path
 * @returns The interpolated position at the given fraction
 */
export const findPointAtFraction = (positions: PositionInterface[], fraction: number): PositionInterface | null => {
  if (!positions.length) return null;
  // Handle edge cases
  if (fraction <= 0) return positions[0];
  if (fraction >= 1 || positions.length === 1) return positions[positions.length - 1];

  // Convert positions to GeoJSON format that Turf.js expects
  const lineString = turfLineString(positions.map((pos) => [pos.lng, pos.lat]));

  // For simple two-point paths or complex polylines, the approach is the same with Turf
  const totalLength = turfLength(lineString, { units: 'kilometers' });
  const targetDistance = totalLength * fraction;

  // Use along() to find a point at the specified distance along the line
  const pointAlong = turfAlong(lineString, targetDistance, { units: 'kilometers' });

  // Convert back to PositionInterface format
  return {
    lat: pointAlong.geometry.coordinates[1],
    lng: pointAlong.geometry.coordinates[0],
  };
};

/**
 * Finds the bearing at a specific fraction along a polyline path
 * @param positions The positions that make up the polyline
 * @param fraction A number between 0 and 1 representing how far along the path
 * @returns The bearing at that position in degrees (0-360)
 */
export const findBearingAtFraction = (positions: PositionInterface[], fraction: number): number => {
  // Handle edge cases
  if (positions.length <= 1) return 0;
  if (fraction <= 0) return calculateBearing(positions[0], positions[1]);
  if (fraction >= 1) return calculateBearing(positions[positions.length - 2], positions[positions.length - 1]);

  // For complex polylines with multiple segments
  // Find the segment that contains the target point using the same logic as findPointAtFraction
  const totalDistance = calculatePathDistance(positions);
  const targetDistance = totalDistance * fraction;

  let distanceSoFar = 0;
  let targetSegmentIndex = 0;

  for (let i = 0; i < positions.length - 1; i += 1) {
    const segmentDistance = distanceInKmBetweenEarthCoordinates(positions[i], positions[i + 1]);
    if (distanceSoFar + segmentDistance >= targetDistance) {
      targetSegmentIndex = i;
      break;
    }
    distanceSoFar += segmentDistance;
  }

  // Return the bearing for this segment
  const heading = calculateBearing(positions[targetSegmentIndex], positions[targetSegmentIndex + 1]);

  // Normalize to 0-360
  return (heading + 360) % 360;
};
